import { getCurrency } from "../utils/localStorage";

// ---------------------------------------------------------------------------
// BetArabia wallet-currency policy.
//
// BetArabia do not convert between wallet currencies, so a player on a non-USD
// wallet (LBP, as of Sept 2026) cannot stake in Champion's Choice at all. The
// backend rejects the join with a generic error, so we detect it in the client
// and tell the player plainly instead.
//
// >>> THESE THREE VALUES ARE THE ONES TO CHANGE IF BETARABIA'S POSITION MOVES.
// Everything else in this file is mechanism. If LBP becomes playable, or a
// second currency is allowed, or the wording needs softening, edit here and
// nowhere else - the modal and both join call sites read from these.
// ---------------------------------------------------------------------------
export const SUPPORTED_WALLET_CURRENCY = "USD";

export const UNSUPPORTED_CURRENCY_TITLE = "Wallet not supported";

export const UNSUPPORTED_CURRENCY_MESSAGE =
  "Champion's Choice games are only available through USD wallets.";
// ---------------------------------------------------------------------------

export const CURRENCY_UNKNOWN = "unknown";
export const CURRENCY_SUPPORTED = "supported";
export const CURRENCY_UNSUPPORTED = "unsupported";

// Resolve from the live URL first, then from storage.
//
// The URL is authoritative at launch, and reading it directly makes the check
// independent of effect ordering: React flushes child effects before parent
// ones, so a mount-time check in a routed page runs BEFORE App.js has had a
// chance to persist the param. Storage is the fallback that carries the value
// across the "/" -> "/game" redirect, which drops the query string.
const readWalletCurrency = () => {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("currency");
    if (fromUrl) return fromUrl;
  } catch (e) {
    // URLSearchParams on a malformed search string - fall through to storage.
  }
  return getCurrency();
};

// Three states, deliberately. BetArabia's launch URL carries
// currency={TGLab_players_currency}, but that placeholder is not guaranteed to
// resolve on every entry point. We only block on a currency we positively know
// is not USD - an absent or unresolved value must let the player through, so a
// broken launch URL never locks out a legitimate USD wallet.
export const getWalletCurrencyStatus = () => {
  const raw = readWalletCurrency();
  if (!raw || typeof raw !== "string") return CURRENCY_UNKNOWN;

  const value = raw.trim().toUpperCase();
  // An uninterpolated template arrives literally as "{TGLAB_PLAYERS_CURRENCY}".
  // That is a launch-URL fault, not a foreign wallet - treat it as unknown.
  if (!value || value.startsWith("{")) return CURRENCY_UNKNOWN;

  return value === SUPPORTED_WALLET_CURRENCY
    ? CURRENCY_SUPPORTED
    : CURRENCY_UNSUPPORTED;
};

export const isJoinBlockedByCurrency = () =>
  getWalletCurrencyStatus() === CURRENCY_UNSUPPORTED;

// Instrumentation so we can see how often this actually fires once live.
// `context` identifies which join path was blocked ("public-game" /
// "private-game") so the two can be told apart in GTM.
export const logCurrencyBlock = (context) => {
  const currency = readWalletCurrency() || "(absent)";

  console.info(
    `[ChampionsChoice] Join blocked - unsupported wallet currency "${currency}" (${context})`
  );

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "Join Blocked - Unsupported Currency",
    walletCurrency: currency,
    blockContext: context,
  });
};
